import React from "react";
import rootStores from "../../stores/index";
import "./Header.css";
import print_icon from "../../assets/print_icon.svg";
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Snackbar, TextField, Typography } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import FilterListRoundedIcon from "@mui/icons-material/FilterListRounded";
import { Popover } from "@mui/material";
import FilterPopup from "./FilterPopup";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import { toJS } from "mobx";
import { observer } from "mobx-react";
import { GridRowId } from "@mui/x-data-grid";
import { useTranslation } from "react-i18next";
import { getAllEventsByClub } from "../../fetchers/RFIDTickets";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";

const RFIDTicketsStore = rootStores.RFIDTicketsStore;
const AuthStore = rootStores.AuthStore;
const navBarStore = rootStores.NavBarStore;

type Props = {
  handleFilter: () => void;
  setIsRightDialogOpen?: any;
  isRightDialogOpen?: boolean;
  onSelectTen?: () => void;
  selectedRows?: GridRowId[];
  filterFields?: string[];
  isSyncing?: boolean;
  isSyncLoading?: boolean;
  handleSync?: () => void;
};

const count = (value: any): number => {
  return value ? 1 : 0;
};

function Header(props: Props) {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language;
  const isLTR = currentLang === "en"; // English = left-to-right alignment
  const textDirection = isLTR ? "ltr" : "rtl";

  const { setClub, filter, setFilterField } = RFIDTicketsStore;
  const clubs = toJS(RFIDTicketsStore.clubs);
  const sortData = toJS(filter);
  const club = toJS(RFIDTicketsStore.club);

  const { handleFilter, setIsRightDialogOpen, isRightDialogOpen, onSelectTen, selectedRows, filterFields } = props;
  const [syncCooldown, setSyncCooldown] = React.useState(0);
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);
  const [isSyncConfirmOpen, setIsSyncConfirmOpen] = React.useState(false);
  const [isSelectEventNotificationOpen, setIsSelectEventNotificationOpen] = React.useState(false);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);
  const onApplyFilter = () => {
    handleClose();
    handleFilter();
  };
  const onPrintIcon = () => setIsRightDialogOpen && setIsRightDialogOpen(!isRightDialogOpen);

  const onSearchFieldKeyDown = (ev: any) => {
    if (ev.key === "Enter") {
      handleFilter();
      ev.preventDefault();
    }
  };

  const fetchEventsByClub = (clubPrefix?: string) => {
    if (!clubPrefix || clubPrefix === "ADMIN") {
      RFIDTicketsStore.setEvents([]);
      return;
    }
  
    getAllEventsByClub(clubPrefix)
      .then(events => {
        RFIDTicketsStore.setEvents(events);
      })
      .catch(console.error);
  };

  const advancedFilterApplied: number =
    count(sortData.area) +
    count(sortData.priceBand) +
    count(sortData.fromDate) +
    count(sortData.toDate);

  const selectCount = 20 - (selectedRows?.length ?? 0);
  const isAdmin = AuthStore?.clubPrefix === "ADMIN";

  React.useEffect(() => {
    if (syncCooldown <= 0) return;

    const timer = setInterval(() => {
      setSyncCooldown(prev => prev - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [syncCooldown]);

  const onSyncClick = async () => {
    if (!props.handleSync || syncCooldown > 0) return;
    if (!sortData?.event) {
      setIsSelectEventNotificationOpen(true);
      return;
    }

    setIsSyncConfirmOpen(true);
  };

  const onConfirmSync = async () => {
    if (!props.handleSync || syncCooldown > 0) return;

    setIsSyncConfirmOpen(false);
    await props.handleSync();
    setSyncCooldown(15);
  };

  const selectedEvent: any = sortData?.event;
  const selectedEventName = selectedEvent?.eventTitle ?? selectedEvent?.title ?? "-";
  const selectedEventCode = selectedEvent?.eventId ?? selectedEvent?._ID ?? "-";
  const selectedEventDateValue =
    selectedEvent?.eventTime ??
    selectedEvent?.eventDate ??
    selectedEvent?.Date ??
    selectedEvent?.timeStamp;
  const selectedEventDate = selectedEventDateValue
    ? new Date(selectedEventDateValue).toLocaleString(isLTR ? "en-US" : "he-IL")
    : "-";

  return (
    <div className="header-container">
      <div className="header-title">
        <div
          className="table-name"
          style={{ textAlign: isLTR ? "left" : "right" }}
        >
          {t(navBarStore.selectedTab.label)}
        </div>
        {!isRightDialogOpen && (
          <div className="print-icon" onClick={onPrintIcon}>
            <img src={print_icon} alt="print icon" />
          </div>
        )}
      </div>

      <div className="search-filter-container">
        <div style={{ width: "100%", height: "40px", display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
          {isAdmin ? (
            <Autocomplete
              size="small"
              style={{ width: "100%" }}
              options={clubs}
              getOptionLabel={(option: any) => option?.name}
              autoComplete
              disableClearable
              value={club ? club : null}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t("navbar.club")}
                  InputProps={{
                    ...params.InputProps,
                    inputProps: {
                      ...params.inputProps,
                      style: { textAlign: isLTR ? "left" : "right" },
                    },
                  }}
                />
              )}
              isOptionEqualToValue={(option, value) => option?.clubPrefix === value?.clubPrefix}
              onChange={(event: any, newValue: any) => {
                const clubPrefix = newValue?.clubPrefix;
              
                setFilterField("clubPrefix", clubPrefix);
                setFilterField("event", null);
                setClub(newValue);
              
                fetchEventsByClub(clubPrefix);
              }}
            />
          ) : (
            <h2 style={{ margin: 0, textAlign: isLTR ? "left" : "right" }}>{clubs[0]?.name}</h2>
          )}
        </div>

        <Box display={"flex"} flexDirection={"row-reverse"} width={"100%"} gap={"10px"}>
          <TextField
            size="small"
            sx={{ flex: 1 }}
            className="search-in-table"
            placeholder={t("navbar.search")}
            disabled={!!props.isSyncLoading}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              inputProps: {
                style: { textAlign: isLTR ? "left" : "right" },
              },
            }}
            onKeyDown={onSearchFieldKeyDown}
            onChange={(e) => setFilterField("searchPhrase", e.target.value)}
          />

          <Button variant="outlined" onClick={() => handleFilter()} disabled={!!props.isSyncLoading}>
            {t("navbar.search")}
          </Button>

          {filterFields?.length && (
            <Box className="filter-botton-container" style={{ position: "relative" }}>
              <Button
                variant="outlined"
                size="small"
                style={{ height: "100%" }}
                onClick={handleClick}
                disabled={!!props.isSyncLoading}
                endIcon={<FilterListRoundedIcon color="primary" />}
              >
                {t("navbar.filter")}
                {advancedFilterApplied > 0 && (
                  <div className="filter-dot" style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                    <div>
                      <Typography variant="caption" sx={{ color: "white", fontSize: "10px" }}>
                        {advancedFilterApplied}
                      </Typography>
                    </div>
                  </div>
                )}
              </Button>

              <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
              >
                <FilterPopup
                  open={open}
                  handleClose={handleClose}
                  onClickFilter={onApplyFilter}
                  sortData={sortData}
                  onFieldChanged={setFilterField}
                  filterFields={filterFields}
                />
              </Popover>
            </Box>
          )}

          {onSelectTen && (
            <Button
              onClick={onSelectTen}
              variant="outlined"
              sx={{ maxHeight: "40px", whiteSpace: "nowrap", maxWidth: "90px" }}
              disabled={selectCount === 0}
            >
              {`${20 - selectCount}/${20} ${t("navbar.selected")}`}
            </Button>
          )}

          {
            props.isSyncing &&
            <Button
              variant="outlined"
              onClick={onSyncClick}
              disabled={syncCooldown > 0 || !!props.isSyncLoading}
              sx={{ maxHeight: "40px", whiteSpace: "nowrap", minWidth: "40px", maxWidth: "40px" }}
            >
              <PlayArrowIcon />
            </Button>
          }
        </Box>
      </div>

      <Dialog
        open={isSyncConfirmOpen}
        onClose={() => setIsSyncConfirmOpen(false)}
        fullWidth
        maxWidth="sm"
        dir={textDirection}
        PaperProps={{ sx: { direction: textDirection } }}
      >
        <DialogTitle sx={{ textAlign: "center", fontWeight: 700, pb: 1 }}>
          {t("ticketsCompare.syncConfirmTitle")}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ textAlign: isLTR ? "left" : "right" }}>
            {t("ticketsCompare.eventName")}: {selectedEventName}
          </Typography>
          <Typography sx={{ textAlign: isLTR ? "left" : "right" }}>
            {t("ticketsCompare.eventCode")}: {selectedEventCode}
          </Typography>
          <Typography sx={{ textAlign: isLTR ? "left" : "right" }}>
            {t("ticketsCompare.eventDate")}: {selectedEventDate}
          </Typography>
          <Typography sx={{ mt: 1.5, mb: 2, textAlign: isLTR ? "left" : "right" }}>
            {t("ticketsCompare.syncConfirmMessage")}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", gap: 1.5, pb: 2, px: 3 }}>
          <Button
            onClick={() => setIsSyncConfirmOpen(false)}
            variant="outlined"
            size="large"
            sx={{ minWidth: 120 }}
          >
            {t("ticketsCompare.cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={onConfirmSync}
            size="large"
            sx={{ minWidth: 120 }}
          >
            {t("ticketsCompare.confirm")}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={isSelectEventNotificationOpen}
        autoHideDuration={5000}
        onClose={() => setIsSelectEventNotificationOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setIsSelectEventNotificationOpen(false)}
          severity="warning"
          variant="filled"
        >
          {t("ticketsCompare.selectEventError")}
        </Alert>
      </Snackbar>
    </div>
  );
}

export default observer(Header);
